import { Stack, Typography, useTheme } from "@mui/material";
import { Users } from "lucide-react";
import { useState } from "react";
import { type Control, useFieldArray, useWatch } from "react-hook-form";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { InsuredPersonDrawer } from "web_myplann/Portfolio/Forms/LifeInsuranceForm/InsuredPersons/InsuredPersonDrawer";
import {
	INSURED_PERSON_DEFAULT_VALUES,
	type InsuredPersonInput,
} from "web_myplann/Portfolio/Forms/LifeInsuranceForm/InsuredPersons/insuredPersonSchema";
import type {
	LifeInsuranceFormInput,
	LifeInsuranceFormOutput,
} from "web_myplann/Portfolio/Forms/LifeInsuranceForm/lifeInsuranceFormSchema";
import { AddRowButton } from "web_myplann/Shared/Components/AddRowButton";
import { ListRow } from "web_myplann/Shared/Components/ListRow";
import { normalizeBirthNumber } from "web_myplann/Shared/Utils/birthNumber";

type Props = {
	control: Control<LifeInsuranceFormInput, unknown, LifeInsuranceFormOutput>;
};

const PageResources = Resources.Portfolio.AddLifeInsurance;

export const InsuredPersonsSection = ({ control }: Props) => {
	const { t } = useResource();
	const theme = useTheme();
	const [editedPerson, setEditedPerson] = useState<number | null>(null);
	const [isPersonOpen, setIsPersonOpen] = useState(false);

	const persons = useFieldArray({ control, name: "insuredPersons" });
	const personValues = useWatch({ control, name: "insuredPersons" }) ?? [];

	const openPerson = (index: number | null) => {
		setEditedPerson(index);
		setIsPersonOpen(true);
	};

	const savePerson = (person: InsuredPersonInput) => {
		if (editedPerson === null) {
			persons.append(person);
			return;
		}
		persons.update(editedPerson, person);
	};

	const takenIdentificationNumbers = personValues
		.filter((_, index) => index !== editedPerson)
		.map((person) => normalizeBirthNumber(person.identificationNumber))
		.filter(Boolean);

	return (
		<>
			<Typography variant="sectionLabel">
				{t(PageResources.Sections.insuredPersons)}
			</Typography>

			<Stack bgcolor={theme.palette.background.paper}>
				{persons.fields.map((field, index) => (
					<ListRow
						key={field.id}
						title={[
							personValues[index]?.firstname,
							personValues[index]?.lastname,
						]
							.filter(Boolean)
							.join(" ")}
						subtitle={t(PageResources.InsuredPersons.riskCount, {
							count: personValues[index]?.risks.length ?? 0,
						})}
						icon={<Users size={18} color={theme.palette.blue[600]} />}
						iconBg={theme.palette.blue[50]}
						showChevron
						isLast={index === persons.fields.length - 1}
						onClick={() => openPerson(index)}
					/>
				))}
				<AddRowButton
					label={t(PageResources.InsuredPersons.addPerson)}
					onClick={() => openPerson(null)}
				/>
			</Stack>

			<InsuredPersonDrawer
				open={isPersonOpen}
				onClose={() => setIsPersonOpen(false)}
				defaultValues={
					editedPerson === null
						? INSURED_PERSON_DEFAULT_VALUES
						: (personValues[editedPerson] ?? INSURED_PERSON_DEFAULT_VALUES)
				}
				takenIdentificationNumbers={takenIdentificationNumbers}
				onSave={savePerson}
				onRemove={
					editedPerson === null ? undefined : () => persons.remove(editedPerson)
				}
			/>
		</>
	);
};
